import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export const useIsDesktop = () => useMediaQuery('(min-width: 768px)');
export const useIsWide = () => useMediaQuery('(min-width: 1200px)');

const SWIPE_MIN_PX = 70;
const SWIPE_MAX_MS = 600;

// Horizontal swipe detection on an element. Ignores mostly vertical moves and
// gestures that start in text fields or elements marked with data-noswipe.
export function useSwipe(ref, { onLeft, onRight }) {
  const handlers = useRef({ onLeft, onRight });
  handlers.current = { onLeft, onRight };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let start = null;
    const onStart = (e) => {
      const t = e.touches[0];
      if (e.touches.length !== 1 || e.target.closest('input, textarea, [data-noswipe]')) { start = null; return; }
      start = { x: t.clientX, y: t.clientY, at: Date.now() };
    };
    const onEnd = (e) => {
      if (!start) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      const quick = Date.now() - start.at < SWIPE_MAX_MS;
      start = null;
      if (!quick || Math.abs(dx) < SWIPE_MIN_PX || Math.abs(dx) < 2 * Math.abs(dy)) return;
      if (dx < 0) handlers.current.onLeft?.();
      else handlers.current.onRight?.();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchend', onEnd);
    };
  }, [ref]);
}

// Lets pages open the right-hand profile panel (e.g. tapping the avatar in a chat header)
export const LayoutContext = createContext({ openPanel: () => {}, closePanel: () => {}, panelOpen: false, isDesktop: true });
export const useLayout = () => useContext(LayoutContext);

// Back button: one step back in the app history. Without an earlier in-app entry
// (page opened directly), go to the fallback instead of leaving the app.
// Pushing the parent page instead would create loops like profile -> settings -> profile -> settings.
export function useGoBack(fallback) {
  const navigate = useNavigate();
  return useCallback(() => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  }, [navigate, fallback]);
}
